import { Backdrop } from '@mui/material';
import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import { activateKioskAction, setKioskErrorAction } from 'store/actions/kiosk-session-actions';
import { costCentersDropdownItemsSelector } from 'store/selectors/company-cost-centers-selectors';
import { locationsDropdownItemsSelector, locationsLoadingSelector } from 'store/selectors/company-locations-selectors';
import { kioskErrorSelector, kioskLoadingSelector } from 'store/selectors/kiosk-session-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  GENERAL__CANCEL_BUTTON,
  GENERAL__SELECT,
  KIOSK__ACTIVATE_BUTTON,
  KIOSK__ACTIVATION_SUBTITLE,
  KIOSK__ACTIVATION_TITLE,
  KIOSK__COST_CENTRE_HINT,
  KIOSK__COST_CENTRE_LABEL,
  KIOSK__LOCATION_LABEL,
  KIOSK__NAME_LABEL,
  KIOSK__NAME_PLACEHOLDER,
  KIOSK__NO_LOCATIONS,
} from 'translations/constants';
import ModalProps from 'types/common/ModalProps';
import {
  KioskEmptyState,
  KioskErrorMessage,
  KioskFieldContainer,
  KioskFieldHint,
  KioskModalActions,
  KioskModalContainer,
  KioskModalStyled,
  KioskModalSubtitle,
  KioskModalTitle,
} from './styles';

interface IActivationForm {
  name: string;
  locationId: string;
  costCentreId: string;
}

const DEFAULT_VALUES: IActivationForm = { name: '', locationId: '', costCentreId: '' };

const KioskActivationModal = ({ open, onClose }: ModalProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const profile = useAppSelector(profileSelector);
  const locations = useAppSelector(locationsDropdownItemsSelector);
  const locationsLoading = useAppSelector(locationsLoadingSelector);
  const costCentres = useAppSelector(costCentersDropdownItemsSelector);
  const loading = useAppSelector(kioskLoadingSelector);
  const error = useAppSelector(kioskErrorSelector);

  const companyId = Number(profile?.companyId || profile?.currentCompany) || 0;

  const { control, handleSubmit, reset, setValue, watch } = useForm<IActivationForm>({ defaultValues: DEFAULT_VALUES });

  const selectedLocationId = watch('locationId');

  // Load the manager's assigned locations whenever the modal opens (includeAllForManager = false
  // returns exactly the locations this manager is assigned to).
  useEffect(() => {
    if (open && companyId) {
      dispatch(setKioskErrorAction(null));
      reset(DEFAULT_VALUES);
      dispatch(getLocationsDropdownItemsAction({ companyId, includeAllForManager: false }));
    }
  }, [open, companyId, dispatch, reset]);

  // Single accessible location => pre-select it.
  useEffect(() => {
    if (open && locations.length === 1) {
      setValue('locationId', String(locations[0].value));
    }
  }, [open, locations, setValue]);

  // Cost centres are scoped to the selected location; changing location clears the chosen one.
  useEffect(() => {
    if (selectedLocationId) {
      setValue('costCentreId', '');
      dispatch(getCostCentersDropdownItemsAction({ companyId, locationId: Number(selectedLocationId) }));
    }
  }, [selectedLocationId, companyId, dispatch, setValue]);

  const handleClose = (event: any) => {
    dispatch(setKioskErrorAction(null));
    onClose(event);
  };

  // On success the kiosk gate flips to ACTIVE and this modal unmounts with the rest of the app shell.
  const onSubmit = (data: IActivationForm) => {
    dispatch(
      activateKioskAction({
        locationId: Number(data.locationId),
        costCentreId: data.costCentreId ? Number(data.costCentreId) : undefined,
        name: data.name.trim() ? data.name.trim() : undefined,
      }),
    );
  };

  const showEmptyState = !locationsLoading && locations.length === 0;

  return (
    // Matches the zIndex used by the other kiosk modals' backdrops (KioskDeactivateModal, KioskNoPinNotice).
    <Backdrop open={open} sx={{ zIndex: 4 }}>
      <KioskModalStyled open={open} onClose={handleClose}>
        <KioskModalContainer>
          <KioskModalTitle>{t(KIOSK__ACTIVATION_TITLE)}</KioskModalTitle>
          <KioskModalSubtitle>{t(KIOSK__ACTIVATION_SUBTITLE)}</KioskModalSubtitle>

          {showEmptyState ? (
            <KioskEmptyState>{t(KIOSK__NO_LOCATIONS)}</KioskEmptyState>
          ) : (
            <KioskFieldContainer>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <UPInput
                    value={field.value}
                    onChange={field.onChange}
                    label={t(KIOSK__NAME_LABEL)}
                    placeholder={t(KIOSK__NAME_PLACEHOLDER)}
                    maxLength={100}
                    id="kiosk-activation-name"
                  />
                )}
              />

              <Controller
                name="locationId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPDropdown
                    value={field.value}
                    onChange={event => field.onChange(String(event.target.value))}
                    placeholder={t(GENERAL__SELECT)}
                    items={locations}
                    label={t(KIOSK__LOCATION_LABEL)}
                    required
                    id="kiosk-activation-location"
                  />
                )}
              />

              <Controller
                name="costCentreId"
                control={control}
                render={({ field }) => (
                  <UPDropdown
                    value={field.value}
                    onChange={event => field.onChange(String(event.target.value))}
                    placeholder={t(GENERAL__SELECT)}
                    items={costCentres}
                    label={t(KIOSK__COST_CENTRE_LABEL)}
                    isFilter
                    disabled={!selectedLocationId}
                    id="kiosk-activation-cost-centre"
                  />
                )}
              />
              <KioskFieldHint>{t(KIOSK__COST_CENTRE_HINT)}</KioskFieldHint>
            </KioskFieldContainer>
          )}

          {error && <KioskErrorMessage>{t(error)}</KioskErrorMessage>}

          <KioskModalActions>
            <UPButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={handleClose} disabled={loading} />
            {!showEmptyState && (
              <UPButton
                text={t(KIOSK__ACTIVATE_BUTTON)}
                onClick={handleSubmit(onSubmit)}
                disabled={!selectedLocationId || loading}
                id="kiosk-activation-submit"
              />
            )}
          </KioskModalActions>
        </KioskModalContainer>
      </KioskModalStyled>
    </Backdrop>
  );
};

export default KioskActivationModal;
